import React, { useEffect, useState } from 'react';
import {
  Paper,
  Typography,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  Box,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  People as PeopleIcon,
  Email as EmailIcon,
  Description as DescriptionIcon,
  Create as CreateIcon,
  TextFields as TextFieldsIcon,
  Markunread as UnreadIcon,
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
  PlayCircleOutline as InProgressIcon,
  Circle as OpenIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { dashboardApi } from '../services/api';
import type { DashboardStats, Activity } from '../types/api';
import EmailCategoryWidget from '../components/EmailCategoryWidget';
import TodayTicketsWidget from '../components/TodayTicketsWidget';
import MitteilungenWidget from '../components/MitteilungenWidget';
import DashboardFristenWidget from '../components/DashboardFristenWidget';

interface StatCardProps {
  title: string;
  value: number;
  icon: React.ReactNode;
  color: string;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, icon, color }) => (
  <Card>
    <CardContent>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Box>
          <Typography color="textSecondary" gutterBottom variant="body2">
            {title}
          </Typography>
          <Typography variant="h4">{value}</Typography>
        </Box>
        <Box
          sx={{
            backgroundColor: color,
            borderRadius: '50%',
            p: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {icon}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [statsResponse, activitiesResponse] = await Promise.all([
        dashboardApi.getStats(),
        dashboardApi.getActivities(10),
      ]);

      setStats(statsResponse.data);
      setActivities(activitiesResponse.data);
    } catch (err: any) {
      console.error('Dashboard-Fehler:', err);
      setError(
        err.response?.data?.message ||
          'Fehler beim Laden der Dashboard-Daten. Ist die API gestartet?'
      );
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Typography variant="body2" color="textSecondary">
          Stellen Sie sicher, dass die API unter https://localhost:7000 läuft.
        </Typography>
      </Box>
    );
  }

  if (!stats) {
    return <Alert severity="warning">Keine Daten verfügbar</Alert>;
  }

  return (
    <Box>
      {/* Welcome Banner mit Logo */}
      <Paper
        sx={{
          p: 4,
          mb: 4,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          borderRadius: 2,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', mb: 1 }}>
              Willkommen zurück!
            </Typography>
            <Typography variant="h6" sx={{ opacity: 0.9 }}>
              Ihr Skeena Dashboard
            </Typography>
          </Box>
          <Box
            sx={{
              display: { xs: 'none', md: 'block' },
              opacity: 0.2,
              position: 'absolute',
              right: -20,
              top: '50%',
              transform: 'translateY(-50%)',
            }}
          >
            <img src="/icon.svg" alt="Skeena" style={{ height: '200px', filter: 'brightness(0) invert(1)' }} />
          </Box>
        </Box>
      </Paper>

      <MitteilungenWidget />

      <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
        Übersicht
      </Typography>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 3, mb: 4 }}>
        <StatCard
          title="Adressen"
          value={stats.anzahlAdressen}
          icon={<PeopleIcon sx={{ color: 'white' }} />}
          color="#1976d2"
        />
        <StatCard
          title="E-Mails"
          value={stats.anzahlEmails}
          icon={<EmailIcon sx={{ color: 'white' }} />}
          color="#2e7d32"
        />
        <StatCard
          title="Ungelesen"
          value={stats.anzahlUngeleseneEmails}
          icon={<UnreadIcon sx={{ color: 'white' }} />}
          color="#ed6c02"
        />
        <StatCard
          title="Vorlagen"
          value={stats.anzahlVorlagen}
          icon={<CreateIcon sx={{ color: 'white' }} />}
          color="#9c27b0"
        />
        <StatCard
          title="Textbausteine"
          value={stats.anzahlTextbausteine}
          icon={<TextFieldsIcon sx={{ color: 'white' }} />}
          color="#0288d1"
        />
        <StatCard
          title="Dokumente"
          value={stats.anzahlGenerierteDokumente}
          icon={<DescriptionIcon sx={{ color: 'white' }} />}
          color="#d32f2f"
        />
      </Box>

      <Typography variant="h5" gutterBottom sx={{ mt: 2, mb: 2 }}>
        Meine Tickets
      </Typography>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr 1fr 1fr' }, gap: 3, mb: 4 }}>
        <StatCard
          title="Gesamt"
          value={stats.anzahlTickets}
          icon={<AssignmentIcon sx={{ color: 'white' }} />}
          color="#1976d2"
        />
        <StatCard
          title="Offen"
          value={stats.anzahlTicketsOffen}
          icon={<OpenIcon sx={{ color: 'white' }} />}
          color="#ff9800"
        />
        <StatCard
          title="In Bearbeitung"
          value={stats.anzahlTicketsInBearbeitung}
          icon={<InProgressIcon sx={{ color: 'white' }} />}
          color="#2196f3"
        />
        <StatCard
          title="Erledigt"
          value={stats.anzahlTicketsErledigt}
          icon={<CheckCircleIcon sx={{ color: 'white' }} />}
          color="#4caf50"
        />
        <StatCard
          title="Überfällig"
          value={stats.anzahlTicketsUeberfaellig}
          icon={<WarningIcon sx={{ color: 'white' }} />}
          color="#f44336"
        />
      </Box>

      <TodayTicketsWidget />

      <DashboardFristenWidget />

      <EmailCategoryWidget />

      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Letzte Aktivitäten
        </Typography>
        {activities.length > 0 ? (
          <List>
            {activities.map((activity, index) => (
              <ListItem key={index} divider={index < activities.length - 1}>
                <ListItemText
                  primary={activity.titel}
                  secondary={
                    <>
                      <Typography
                        component="span"
                        variant="body2"
                        color="textSecondary"
                      >
                        {activity.beschreibung}
                      </Typography>
                      {' • '}
                      <Typography
                        component="span"
                        variant="body2"
                        color="textSecondary"
                      >
                        {new Date(activity.zeitpunkt).toLocaleString('de-DE')}
                      </Typography>
                    </>
                  }
                />
              </ListItem>
            ))}
          </List>
        ) : (
          <Typography color="textSecondary">
            Keine Aktivitäten vorhanden
          </Typography>
        )}
      </Paper>
    </Box>
  );
};

export default Dashboard;
